// src/components/payment/StripeCheckout.jsx
import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../../../../slices/authSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  Clock,
} from "lucide-react";
import { Loader } from "../../../base/Loader";
import { BACKEND_STRIPE_KEY, BACKEND_STRIPE_URL } from "../../../../config";
import { cancelJobIfNoShovellerApplied } from "../../../../apiManager/houseOwner/matchShvoller";
import { toast } from "sonner";

const stripePromise = loadStripe(BACKEND_STRIPE_KEY);
const baseUrl = BACKEND_STRIPE_URL;

export function StripeCheckout() {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canceled, setCanceled] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth);
  const token = userInfo ? userInfo.token : null;
  const jobId = userInfo.user.jobId;
  const dispatch = useDispatch();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const canceledParam = urlParams.get("canceled");

    if (canceledParam) {
      setCanceled(true);
      setLoading(false);
      return;
    }

    if (sessionId) {
      const checkPaymentStatus = async () => {
        try {
          const response = await fetch(`${baseUrl}/verifyStatus`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ sessionId }),
          });

          if (!response.ok) {
            throw new Error("Failed to fetch payment status");
          }

          const data = await response.json();
          dispatch(setCredentials({ ...data }));
          setPaymentStatus(data.user.paymentStatus);
        } catch (err) {
          console.error("Error fetching payment status:", err);
          setError("An error occurred while checking the payment status.");
        } finally {
          setLoading(false);
        }
      };

      checkPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [dispatch, token]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/checkout?type=single&capture=instant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            uri: "/homeowner/post",
            jobs: [
              {
                amount: userInfo.user.paymentOffering,
                jobId: userInfo.user.jobId,
              },
            ],
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create checkout session: ${errorText}`);
      }

      const { id } = await response.json();
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId: id });

      if (error) {
        console.error("Error redirecting to checkout:", error);
        setError(`Failed to redirect to checkout: ${error.message}`);
      }
    } catch (error) {
      console.error("Error handling checkout:", error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelJobIfNoShovellerApplied(jobId);
      if (res.err) {
        setError(res.err || "An error occurred while canceling the job");
        return;
      }

      const userData = JSON.parse(localStorage.getItem("userInfo"));
      console.log(userData);
      dispatch(
        setCredentials({
          user: {
            id: userData.user.id,
            role: userData.user.role,
          },
          token: userData.token,
        }),
      );
      toast.success("Job scheduling has been cancelled !");
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelLoading(false);
      setShowCancelDialog(false);
    }
  };

  // Loading state
  if (loading) {
    return <Loader />;
  }

  // Error state
  if (error && !canceled && !paymentStatus) {
    return (
      <div className="space-y-12 py-5 md:py-10">
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-5xl font-bold text-black">
            Payment Error
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We encountered an issue while processing your payment
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <CardContent className="p-0 space-y-6 text-center">
              <div className="flex justify-center">
                <XCircle className="w-16 h-16 text-red-500" />
              </div>
              <p className="text-gray-700">{error}</p>
              <Button onClick={handleCheckout} className="w-full">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Canceled state
  if (canceled) {
    return (
      <div className="space-y-12 py-5 md:py-10">
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-5xl font-bold text-black">
            Authorization Cancelled
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your payment authorization was cancelled
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <CardContent className="p-0 space-y-6 text-center">
              <div className="flex justify-center">
                <AlertTriangle className="w-16 h-16 text-amber-500" />
              </div>
              <p className="text-gray-700">
                It looks like you clicked the back button. Would you like to
                cancel posting or try authorizing again?
              </p>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelDialog(true)}
                  className="flex-1"
                >
                  Cancel Posting
                </Button>
                <Button onClick={handleCheckout} className="flex-1">
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-md">
            <div className="flex flex-col items-center text-center space-y-6 p-2">
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <DialogHeader>
                  <DialogTitle className="text-xl text-center font-semibold">
                    Cancel Posting?
                  </DialogTitle>
                  <DialogDescription className="text-sm text-center text-muted-foreground pt-2">
                    If you cancel the posting, you will need to repost the job
                    details, but your previous authorization will not be
                    charged.
                  </DialogDescription>
                </DialogHeader>
              </div>

              <DialogFooter className="flex gap-3 w-full">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelDialog(false)}
                  className="flex-1"
                >
                  Go Back
                </Button>
                <Button
                  onClick={handleCancelJob}
                  disabled={cancelLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {cancelLoading ? "Cancelling..." : "Cancel Posting"}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Payment authorized state
  if (paymentStatus === "authorize") {
    return (
      <div className="space-y-12 py-5 md:py-10">
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-5xl font-bold text-black">
            Payment Authorized!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your job is now confirmed and ready to go
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <Card className="p-8">
            <CardContent className="p-0 space-y-6 text-center">
              <div className="flex justify-center">
                <CheckCircle2 className="w-16 h-16 text-green-500" />
              </div>
              <p className="text-gray-700">
                Thank you! Your payment is authorized and your job is now
                confirmed.
              </p>
              <Button
                onClick={() => navigate("/houseOwner/listOfShovellerApplied")}
                className="w-full"
              >
                Go to Matching Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Default checkout view
  return (
    <div className="space-y-12 py-5 md:py-10">
      <div className="text-center space-y-4">
        <h1 className="text-3xl md:text-5xl font-bold text-black">
          Confirm Payment
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Finalize your payment to publish your job posting.
        </p>
      </div>
      <div className="max-w-md mx-auto">
        <Card className="p-8">
          <CardContent className="p-0 space-y-6">
            {/* Main Content */}
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <CreditCard className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-700">
                Your job details are ready. Please confirm your payment method
                to finalize the posting.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full"
              >
                {loading ? "Processing..." : "Confirm Payment"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                className="w-full"
              >
                Cancel Posting
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center space-y-6 p-2">
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-xl text-center font-semibold">
                  Cancel Posting?
                </DialogTitle>
                <DialogDescription className="text-sm text-center text-muted-foreground pt-2">
                  If you cancel the posting, you will need to repost the job
                  details, but your previous authorization will not be charged.
                </DialogDescription>
              </DialogHeader>
            </div>

            <DialogFooter className="flex gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button
                onClick={handleCancelJob}
                disabled={cancelLoading}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {cancelLoading ? "Cancelling..." : "Cancel Posting"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
